import { useId } from 'react'
import { Input } from './Input'
import { MAX_CATEGORY_LENGTH } from '../../lib/categories'

// CategoryCombobox — a controlled, zero-dependency `<input list>` + `<datalist>`
// combobox over the shared Input. It suggests the merged categories the caller
// passes (defaults + the owner's existing categories + the current value) while
// still allowing a free typed value. Purely presentational: it does no data
// fetching and never calls mergeCategorySuggestions itself — callers own the
// suggestion source (S4's editors, S5's bulk-move dialog) and pass the already
// -merged array. The prop contract is LOCKED here because S4 and S5 both import
// it. (Explicit `?: T | undefined` optional style matches SettingsTab /
// Modal's strict-TS idiom.)
export interface CategoryComboboxProps {
  value: string
  onChange: (v: string) => void
  suggestions: string[]
  placeholder?: string | undefined
  disabled?: boolean | undefined
  id?: string | undefined
  maxLength?: number | undefined // default MAX_CATEGORY_LENGTH
  'aria-label'?: string | undefined
}

export function CategoryCombobox({
  value,
  onChange,
  suggestions,
  placeholder,
  disabled,
  id,
  maxLength,
  'aria-label': ariaLabel,
}: CategoryComboboxProps) {
  // Unique per instance so multiple comboboxes on one page never share a
  // <datalist> id. Prefer the caller-supplied id; otherwise derive from useId.
  const reactId = useId()
  const listId = id ? `${id}-list` : `cat-list-${reactId}`

  return (
    <>
      <Input
        type="text"
        list={listId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={maxLength ?? MAX_CATEGORY_LENGTH}
        disabled={disabled}
        placeholder={placeholder}
        id={id}
        {...(ariaLabel !== undefined ? { 'aria-label': ariaLabel } : {})}
      />
      <datalist id={listId}>
        {suggestions.map((s) => (
          <option key={s} value={s} />
        ))}
      </datalist>
    </>
  )
}
